import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { useCompaniesStore } from '../stores/companiesStore';
import { useOffersStore } from '../stores/offersStore';
import CompanyForm from '../components/CompanyForm';
import CompanyDetails from '../components/CompanyDetails';
import CompanyFilters, { FilterValues } from '../components/company/CompanyFilters';
import ExcelTemplate from '../components/company/ExcelTemplate';
import { Building2, MapPin, Calendar, Upload, Plus, Search, Filter, Trash2, Download, Users, LayoutGrid, List, User, X, Phone, Mail, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useThemeStore } from '../stores/themeStore';

export default function Entreprises() {
  const navigate = useNavigate();
  const { companies, loadCompanies, addCompany, deleteCompany } = useCompaniesStore();
  const { offers } = useOffersStore();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showNewForm, setShowNewForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { darkMode } = useThemeStore();
  const [activeFilters, setActiveFilters] = useState<FilterValues>({
    sectors: [],
    cities: [],
    status: [],
    offerTypes: []
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) {
        navigate('/login');
        return;
      }
      loadCompanies();
    });

    return () => unsubscribe();
  }, [navigate, loadCompanies]);

  const getCompanyOffers = (companyName: string) => {
    return offers.filter(offer => offer.company === companyName);
  };

  const filteredCompanies = companies.filter(company => {
    if (searchTerm) {
      const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           company.sector?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
    }

    if (activeFilters.sectors.length > 0) {
      if (!company.sector || !activeFilters.sectors.includes(company.sector)) return false;
    }

    if (activeFilters.cities.length > 0) {
      const cityMatch = activeFilters.cities.some(filterCity =>
        company.city?.toLowerCase().includes(filterCity.toLowerCase())
      );
      if (!cityMatch) return false;
    }

    if (activeFilters.status.length > 0) {
      const companyStatus = company.status || 'active';
      if (!activeFilters.status.includes(companyStatus as 'active' | 'inactive')) return false;
    }

    if (activeFilters.offerTypes.length > 0) {
      const companyOffers = getCompanyOffers(company.name);
      const hasMatchingOfferType = companyOffers.some(offer =>
        activeFilters.offerTypes.includes(offer.type)
      );
      if (!hasMatchingOfferType) return false;
    }

    return true;
  });

  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const handleImport = async (companies: any[]) => {
    try {
      for (const company of companies) {
        await addCompany(company);
      }
      setShowImport(false);
      toast.success(`${companies.length} entreprise${companies.length > 1 ? 's' : ''} importée${companies.length > 1 ? 's' : ''} avec succès`);
    } catch (error) {
      console.error('Error importing companies:', error);
      toast.error('Erreur lors de l\'import des entreprises');
    }
  };

  const handleExport = () => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour exporter les données');
      return;
    }

    if (selectedCompanies.size === 0) {
      toast.error('Sélectionnez au moins une entreprise à exporter');
      return;
    }

    try {
      const selectedCompaniesList = companies.filter(c => selectedCompanies.has(c.id));
      
      const data = selectedCompaniesList.map(c => ({
        'ID Utilisateur': auth.currentUser?.uid || '',
        'Utilisateur': auth.currentUser?.displayName || 'Non renseigné',
        'Nom': c.name,
        'Secteur': c.sector || '',
        'Taille': c.size || '',
        'Adresse': c.address || '',
        'Ville': c.city || '',
        'Téléphone': c.phone || '',
        'Email': c.email || '',
        'Statut': c.status === 'active' ? 'Active' : 'Inactive',
        'Contacts': c.contacts.map(contact => 
          `${contact.name} (${contact.role}) - ${contact.phone} - ${contact.email}`
        ).join('\n'),
        'Créée par': c.createdBy || 'Non spécifié',
        'Newsletter': c.newsletter_consent ? 'Oui' : 'Non',
        'Date de création': new Date(c.createdAt).toLocaleDateString('fr-FR'),
        'Dernière mise à jour': new Date(c.updatedAt).toLocaleDateString('fr-FR')
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);

      // Ajuster la largeur des colonnes
      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(20, key.length)
      }));
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Entreprises');
      XLSX.writeFile(wb, 'export_entreprises.xlsx');

      toast.success(`${selectedCompanies.size} entreprise${selectedCompanies.size > 1 ? 's' : ''} exportée${selectedCompanies.size > 1 ? 's' : ''} avec succès`);
    } catch (error) {
      console.error('Error exporting companies:', error);
      toast.error('Erreur lors de l\'export des entreprises');
    }
  };

  const handleCardClick = (company: any) => {
    if (!selectedCompanies.size) {
      setSelectedCompany(company);
    }
  };

  // Format phone number for display
  const formatPhone = (phone: string) => {
    if (!phone) return 'Non renseigné';
    return phone;
  };

  // Get primary contact from company
  const getPrimaryContact = (company: any) => {
    if (!company.contacts || company.contacts.length === 0) {
      return null;
    }
    return company.contacts[0];
  };

  // Format phone number for tel: links by removing spaces, dots, etc.
  const formatPhoneForLink = (phone: string) => {
    if (!phone) return '';
    return phone.replace(/[\s\.\-\(\)]/g, '');
  };

  // Create a maps URL for the address
  const getMapsUrl = (address: string) => {
    // Encode the address for URL
    const encodedAddress = encodeURIComponent(address);
    
    return `https://maps.google.com/maps?q=${encodedAddress}`;
  };

  // Fonction pour obtenir les initiales d'une personne
  const getInitials = (name: string): string => {
    if (!name) return '';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // Fonction pour obtenir le nom complet à partir des initiales
  const getFullName = (initials: string, createdBy: string): string => {
    return createdBy || 'Utilisateur inconnu';
  };

  const handleSelectCompany = (id: string) => {
    setSelectedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (activeFilters.sectors.length > 0) count++;
    if (activeFilters.cities.length > 0) count++;
    if (activeFilters.status.length > 0) count++;
    if (activeFilters.offerTypes.length > 0) count++;
    return count;
  };

  const resetFilters = () => {
    setActiveFilters({
      sectors: [],
      cities: [],
      status: [],
      offerTypes: []
    });
    setCurrentPage(1);
  };

  const handleDeleteSelected = async () => {
    if (selectedCompanies.size === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedCompanies.size} entreprise${selectedCompanies.size > 1 ? 's' : ''} ?`)) {
      try {
        for (const id of selectedCompanies) {
          await deleteCompany(id);
        }
        setSelectedCompanies(new Set());
        toast.success(`${selectedCompanies.size} entreprise${selectedCompanies.size > 1 ? 's' : ''} supprimée${selectedCompanies.size > 1 ? 's' : ''} avec succès`);
      } catch (error) {
        console.error('Error deleting companies:', error);
        toast.error('Erreur lors de la suppression des entreprises');
      }
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        'name': 'Exemple Entreprise',
        'sector': 'Informatique',
        'size': 'TPE (1-9 employés)',
        'address': '123 rue Example',
        'city': 'Marseille',
        'phone': '04 91 00 00 00',
        'email': 'contact@exemple.fr',
        'contacts': 'Joe la frite',
        'Contact - téléphone': '06 00 00 00 00',
        'Contact - email': 'contact@exemple.fr'
      }
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(template);

    // Ajuster la largeur des colonnes
    const colWidths = Object.keys(template[0]).map(key => ({
      wch: Math.max(20, key.length)
    }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Entreprises');
    XLSX.writeFile(wb, 'modele_import_entreprises.xlsx');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Entreprises ({companies.length})
        </h1>
        <div className="flex gap-4">
          <button
            onClick={handleExport}
            disabled={selectedCompanies.size === 0}
            className={`hidden md:flex items-center px-4 py-2 text-sm ${darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'} rounded-2xl hover:${darkMode ? 'bg-green-600' : 'bg-green-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter ({selectedCompanies.size})
          </button>
          <button
            onClick={() => setShowImport(true)}
            className={`hidden md:flex items-center px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}
          >
            <Upload className="w-5 h-5 mr-2" />
            Import
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center px-4 py-2 bg-gradient-anthea text-white rounded-2xl hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle entreprise
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher une entreprise..."
            className={`w-full pl-10 pr-4 py-2 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-anthea-blue ${darkMode ? 'bg-gray-700 text-white border-gray-600' : ''}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(true)}
            className={`flex items-center px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} relative`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filtres
            {getActiveFiltersCount() > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={resetFilters}
              className={`flex items-center px-3 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
              title="Réinitialiser les filtres"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <div className={`flex gap-2 border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-2xl p-1`}>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-xl ${viewMode === 'grid' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
            >
              <LayoutGrid className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-xl ${viewMode === 'list' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
            >
              <List className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>
      </div>

      {selectedCompanies.size > 0 && (
        <div className={`flex justify-between items-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
          <div className="flex items-center gap-4">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {selectedCompanies.size} entreprise{selectedCompanies.size > 1 ? 's' : ''} sélectionnée{selectedCompanies.size > 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={handleDeleteSelected}
            className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg hover:${darkMode ? 'bg-red-800' : 'bg-red-100'}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer la sélection
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Affichage de {startIndex + 1} à {Math.min(endIndex, filteredCompanies.length)} sur {filteredCompanies.length} entreprise{filteredCompanies.length > 1 ? 's' : ''}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Afficher</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className={`px-3 py-1.5 text-sm border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>par page</span>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {paginatedCompanies.map((company) => {
            const companyOffers = getCompanyOffers(company.name);
            const primaryContact = getPrimaryContact(company);
            const fullAddress = [company.address, company.city].filter(Boolean).join(', ');
            const initials = getInitials(company.createdBy || '');
            const fullName = getFullName(initials, company.createdBy || '');
            
            return (
              <div
                key={company.id}
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer relative`}
                onClick={() => !selectedCompanies.size && handleCardClick(company)}
              >
                <div className="absolute top-4 left-4 z-10">
                  <div onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedCompanies.has(company.id)}
                      onChange={() => handleSelectCompany(company.id)}
                      className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                    />
                  </div>
                </div>
                <div className="absolute top-4 right-4 z-10">
                  <div 
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'} group relative`}
                    title={fullName}
                  >
                    {initials}
                    <span className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap">
                      {fullName}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className={`font-semibold text-lg mt-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{company.name}</h3>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{company.sector}</p>
                  <div className={`mt-4 space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                      {fullAddress ? (
                        <a 
                          href={getMapsUrl(fullAddress)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'} underline`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="truncate">{fullAddress}</span>
                        </a>
                      ) : (
                        <span className="truncate">Adresse non renseignée</span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                      <a 
                        href={`tel:${formatPhoneForLink(company.phone)}`} 
                        className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatPhone(company.phone)}
                      </a>
                    </div>
                    {primaryContact && (
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <a 
                          href={`mailto:${primaryContact.email || company.email}`} 
                          className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'} truncate`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {primaryContact.email || company.email}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>{company.contacts?.length || 0} contact{company.contacts?.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>
                        {companyOffers.length} offre{companyOffers.length !== 1 ? 's' : ''}
                      </span>
                      <div className="ml-2 flex flex-wrap gap-1">
                        {companyOffers.filter(offer => offer.type === 'CDI').length > 0 && (
                          <span className={`text-xs ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'} px-2 py-0.5 rounded-full`}>
                            {companyOffers.filter(offer => offer.type === 'CDI').length} CDI
                          </span>
                        )}
                        {companyOffers.filter(offer => offer.type === 'CDD').length > 0 && (
                          <span className={`text-xs ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'} px-2 py-0.5 rounded-full`}>
                            {companyOffers.filter(offer => offer.type === 'CDD').length} CDD
                          </span>
                        )}
                        {companyOffers.filter(offer => offer.type === 'Intérim').length > 0 && (
                          <span className={`text-xs ${darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'} px-2 py-0.5 rounded-full`}>
                            {companyOffers.filter(offer => offer.type === 'Intérim').length} Intérim
                          </span>
                        )}
                        {companyOffers.filter(offer => offer.type === 'PMSMP').length > 0 && (
                          <span className={`text-xs ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'} px-2 py-0.5 rounded-full`}>
                            {companyOffers.filter(offer => offer.type === 'PMSMP').length} PMSMP
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {paginatedCompanies.map((company) => {
            const companyOffers = getCompanyOffers(company.name);
            const primaryContact = getPrimaryContact(company);
            const fullAddress = [company.address, company.city].filter(Boolean).join(', ');
            const initials = getInitials(company.createdBy || '');
            const fullName = getFullName(initials, company.createdBy || '');
            
            return (
              <div
                key={company.id}
                className={`flex items-center p-4 hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors cursor-pointer`}
                onClick={() => handleCardClick(company)}
              >
                <div className="mr-4" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedCompanies.has(company.id)}
                    onChange={() => handleSelectCompany(company.id)}
                    className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                  />
                </div>
                <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{company.name}</h3>
                      <div 
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'} group relative`}
                        title={fullName}
                      >
                        {initials}
                        <span className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap">
                          {fullName}
                        </span>
                      </div>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{company.sector}</p>
                    {fullAddress && (
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center mt-1`}>
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        <a 
                          href={getMapsUrl(fullAddress)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'} underline`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="truncate">{fullAddress}</span>
                        </a>
                      </p>
                    )}
                  </div>
                  <div className={`col-span-3 flex flex-col text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                      <a 
                        href={`tel:${formatPhoneForLink(company.phone)}`} 
                        className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatPhone(company.phone)}
                      </a>
                    </div>
                    {primaryContact && (
                      <div className="flex items-center mt-1">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <a 
                          href={`mailto:${primaryContact.email || company.email}`} 
                          className={`hover:${darkMode ? 'text-blue-400' : 'text-blue-600'} truncate`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {primaryContact.email || company.email}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className={`col-span-2 flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                    {company.contacts?.length || 0} contact{company.contacts?.length !== 1 ? 's' : ''}
                  </div>
                  <div className={`col-span-3 flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Briefcase className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>
                      {companyOffers.length} offre{companyOffers.length !== 1 ? 's' : ''}
                    </span>
                    <div className="ml-2 flex flex-wrap gap-1">
                      {companyOffers.filter(offer => offer.type === 'CDI').length > 0 && (
                        <span className={`text-xs ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'} px-2 py-0.5 rounded-full`}>
                          {companyOffers.filter(offer => offer.type === 'CDI').length} CDI
                        </span>
                      )}
                      {companyOffers.filter(offer => offer.type === 'CDD').length > 0 && (
                        <span className={`text-xs ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'} px-2 py-0.5 rounded-full`}>
                          {companyOffers.filter(offer => offer.type === 'CDD').length} CDD
                        </span>
                      )}
                      {companyOffers.filter(offer => offer.type === 'Intérim').length > 0 && (
                        <span className={`text-xs ${darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-700'} px-2 py-0.5 rounded-full`}>
                          {companyOffers.filter(offer => offer.type === 'Intérim').length} Intérim
                        </span>
                      )}
                      {companyOffers.filter(offer => offer.type === 'PMSMP').length > 0 && (
                        <span className={`text-xs ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'} px-2 py-0.5 rounded-full`}>
                          {companyOffers.filter(offer => offer.type === 'PMSMP').length} PMSMP
                        </span>
                      )}
                    </div>
                    {primaryContact && (
                      <span className={`ml-3 text-xs ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} px-2 py-0.5 rounded-full`}>
                        {primaryContact.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-col items-center mt-6 mb-16 gap-4">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Afficher</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className={`border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg p-1 text-sm`}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>entreprises par page</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 text-sm border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-900'} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:${darkMode ? 'bg-gray-600' : 'bg-gray-50'}`}
            >
              Précédent
            </button>

            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        currentPage === page
                          ? 'bg-gradient-anthea text-white'
                          : `border ${darkMode ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50'}`
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return <span key={page} className={`px-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 text-sm border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-900'} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:${darkMode ? 'bg-gray-600' : 'bg-gray-50'}`}
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {showNewForm && (
        <CompanyForm
          onSubmit={(data) => {
            addCompany(data);
            setShowNewForm(false);
          }}
          onClose={() => setShowNewForm(false)}
        />
      )}

      {selectedCompany && (
        <CompanyDetails
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}

      {showFilters && (
        <CompanyFilters
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApplyFilters={(filters) => {
            setActiveFilters(filters);
            setCurrentPage(1);
            setShowFilters(false);
          }}
          currentFilters={activeFilters}
        />
      )}

      {showImport && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Importer des entreprises</h2>
                <button onClick={() => setShowImport(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <button
                    onClick={downloadTemplate}
                    className={`flex items-center px-4 py-2 ${darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-700 bg-gray-100'} rounded-lg hover:${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le modèle
                  </button>

                  <ExcelTemplate onImport={handleImport} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}